'use client';

import posthog from 'posthog-js';

/**
 * Thin typed wrapper around posthog-js so call sites stay terse.
 * Safe to call before init — posthog-js queues events.
 */

export type SetupStep =
  | 'share'
  | 'test_booking'
  | 'business_info'
  | 'employees'
  | 'services'
  | 'opening_hours'
  | 'calendar_connected';

export interface IdentifyProps {
  tenantId?: string | null;
  email?: string | null;
  plan?: string | null;
  trial?: boolean;
  locale?: string | null;
}

export function identifyUser(userId: string, props: IdentifyProps = {}) {
  if (!userId || typeof window === 'undefined') return;
  posthog.identify(userId, {
    email: props.email ?? undefined,
    tenant_id: props.tenantId ?? undefined,
    plan: props.plan ?? undefined,
    is_trial: props.trial ?? undefined,
    locale: props.locale ?? undefined,
  });
  if (props.tenantId) {
    posthog.group('tenant', props.tenantId, {
      plan: props.plan ?? undefined,
      is_trial: props.trial ?? undefined,
    });
  }
}

export function resetIdentity() {
  if (typeof window === 'undefined') return;
  posthog.reset();
}

export function trackEvent(name: string, props?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  posthog.capture(name, props);
}

// Setup-checklist convenience helpers — keep names stable, they're funnel keys in PostHog UI.
export function trackSetupChecklistViewed(props: {
  servicesCount: number;
  openingHoursCount: number;
  hasBusinessInfo: boolean;
  hasCalendar: boolean;
  completedSteps: number;
}) {
  trackEvent('setup_checklist_viewed', props);
}

export function trackSetupStepCompleted(step: SetupStep, extra?: Record<string, unknown>) {
  trackEvent('setup_step_completed', { step, ...extra });
}

export function trackSetupCompleted(props: { durationSinceRegisterMs?: number } = {}) {
  trackEvent('setup_completed', props);
}

export function trackDomainPromo(action: 'viewed' | 'clicked' | 'dismissed') {
  trackEvent('setup_domain_promo', { action });
}

/**
 * Domain-buy funnel — keep names stable, they map to a PostHog
 * funnel: modal_opened → search → search_completed → purchase_started
 * → (premium_confirmed | direct_redirect) → checkout_returned.
 */
export function trackDomainModalOpened(props: { source: string }) {
  trackEvent('domain_modal_opened', props);
}

export function trackDomainSearch(props: { queryLength: number }) {
  trackEvent('domain_search', props);
}

export function trackDomainSearchCompleted(props: {
  queryLength: number;
  resultsCount: number;
  availableCount: number;
  hasPremium: boolean;
  durationMs: number;
}) {
  trackEvent('domain_search_completed', props);
}

export function trackDomainPurchaseStarted(props: {
  domain: string;
  tld: string;
  price: number;
  currency: string;
  isPremium: boolean;
  fromChip?: boolean;
}) {
  trackEvent('domain_purchase_started', props);
}

export function trackDomainPremiumConfirmed(props: {
  domain: string;
  tld: string;
  price: number;
  currency: string;
}) {
  trackEvent('domain_premium_confirmed', props);
}

export function trackDomainPremiumCancelled(props: {
  domain: string;
  tld: string;
  price: number;
  currency: string;
}) {
  trackEvent('domain_premium_cancelled', props);
}

export function trackDomainWatchlistAdded(props: {
  domain: string;
  tld: string;
  reason?: string;
}) {
  trackEvent('domain_watchlist_added', props);
}

export function trackDomainExampleChipClicked(props: { chipLabel: string }) {
  trackEvent('domain_example_chip_clicked', props);
}

/**
 * Landing CTA click — fires when a visitor clicks a primary signup CTA on the
 * marketing site (before navigating to /register). This is the funnel stage
 * between $pageview and signup_started: "did they engage the CTA at all?".
 * `source` mirrors the ?source= param appended to /register so click → register
 * → trial can be attributed to a specific CTA placement. Keep the name stable —
 * it's a funnel key in PostHog.
 */
export function trackLandingCtaClicked(source: string) {
  trackEvent('landing_cta_clicked', { source });
}

/**
 * Trial / Paid conversion funnel — keep these event names stable. They're
 * referenced from a PostHog dashboard funnel (signup_started →
 * signup_completed → plan_picker_viewed → checkout_started → checkout_completed).
 *
 * Drop-off rates between any two events are the primary KPI; renaming
 * silently breaks the dashboard.
 */
export function trackSignupStarted(source: string | undefined) {
  trackEvent('signup_started', { source });
}

export function trackSignupCompleted(props: {
  plan?: string;
  billingCycle?: string;
  businessType?: string;
  /** Self-reported attribution ("How did you hear about us?"). Captures
   *  the AI/word-of-mouth channel that analytics can't see — AI recommends
   *  the brand, the user navigates directly or googles it, so the AI
   *  origin is otherwise invisible (Ahrefs AEO course, 2026). */
  heardAboutUs?: string;
}) {
  // Persist the attribution as a person property too, so it sticks to the
  // profile for cohort/funnel analysis — not just this one event.
  const eventProps =
    typeof window !== 'undefined' && props.heardAboutUs
      ? { ...props, $set: { heard_about_us: props.heardAboutUs } }
      : props;
  trackEvent('signup_completed', eventProps);
}

export function trackPlanPickerViewed(props: {
  status?: string;
  trialDaysRemaining?: number;
  surface: 'subscription_page' | 'trial_banner' | 'dashboard_card' | 'sidebar_pill';
}) {
  trackEvent('plan_picker_viewed', props);
}

export function trackCheckoutStarted(props: {
  planSlug: string;
  billingCycle: 'monthly' | 'yearly';
  keepTrial: boolean;
  mode: 'embedded' | 'hosted';
}) {
  trackEvent('checkout_started', props);
}

export function trackCheckoutCompleted(props: {
  planSlug?: string;
  billingCycle?: string;
  sessionId?: string;
}) {
  trackEvent('checkout_completed', props);
}

export function trackCheckoutCancelled(props: { reason?: string }) {
  trackEvent('checkout_cancelled', props);
}

export function trackSubscriptionReactivated(props: { planSlug?: string }) {
  trackEvent('subscription_reactivated', props);
}
